import * as React from 'react';

// API
import apibridge from '../apibridge';

// UTILS
import { loadScriptByURL, SITE_KEY, validationErrorsToString } from '../utils';

// COMPONENTS
import FilesList from './FilesList';
import GlobalModal from './GlobalModal';

// ICONS
import { ReactComponent as UploadIcon } from '../assets/svg/upload.svg';
import Button from './Button';
import { Link } from 'react-router-dom';

// TYPES
export interface FileType {
	id?: string;
	displayFileName?: string;
	link?: string;
	name?: string;
	size?: number;
	type?: string;
	uploadInProgress?: boolean;
	percentComplete?: number;
}

interface FileUploadTypes {
	keyValue?: string;
	label?: string;
	name: string;
	placeholder?: string;
	onChange?: any;
	multiple?: boolean;
	maxSizeMb?: number; // specify in mb // defaults to 5mb
	imagesOnly?: boolean;
	files?: FileType[];
	disabled?: boolean;
}

export type progressType = (file: FileType, kbComplete: number | undefined) => void;
export type boundProgressType = (kbComplete: number | undefined) => void;

const allAllowedFileTypes = [
	'image/jpg',
	'image/jpeg',
	'image/png',
	'application/pdf'
	// 'application/msword', // .doc, .rtf
	// 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
	// 'application/vnd.oasis.opendocument.text', // .odt google open doc
	// 'text/plain'
];

const imageAllowedFileTypes = ['image/jpg', 'image/jpeg', 'image/png'];

// NOTE: files are always passed in as an array, whether there are 1 or many
// The return for the callback will be a string or an array - depending on the multiple property

export const FileUpload: React.FC<FileUploadTypes> = ({
	keyValue = '',
	label = '',
	name = '',
	placeholder = '',
	onChange,
	multiple = false,
	maxSizeMb = 5,
	imagesOnly = false,
	files,
	disabled = false
}) => {
	const fileInput = React.useRef<HTMLInputElement>(null);
	const [filesInitialised, setFilesInitialised] = React.useState<boolean>(false);
	const previousFileIds = React.useRef('');
	const [selectedFiles, setSelectedFiles] = React.useState<FileType[]>([]);
	const [error, setError] = React.useState<string>('');
	const [documentModal, setDocumentModal] = React.useState<boolean>(false);

	React.useEffect(() => {
		loadScriptByURL('recaptcha-key', function () {
			// console.log('Script Loaded');
		});
	}, []);

	const handleChange = () => {
		if (onChange) {
			const completedFiles = selectedFiles.filter((file) => !file.uploadInProgress);
			// if (!completedFiles.length) return;

			const fileIds = completedFiles.map((file) => file.id);
			if (fileIds.join('') === previousFileIds.current) return;
			previousFileIds.current = fileIds.join('');

			if (multiple) {
				// if multiple - return an array of ids
				onChange(fileIds || [], completedFiles);
			} else {
				// if single - return only id
				onChange(fileIds[0] || '', completedFiles);
			}
		}
	};

	const handleRemove = (id: string) => {
		setSelectedFiles((prevState) => {
			const newFiles = prevState.filter((file) => file.id !== id);

			return newFiles;
		});
	};

	const handleRemoveMultiple = (ids: string[]) => {
		setSelectedFiles((prevState) => {
			const newFiles = prevState.filter((file) => !ids.includes(file.id || ''));
			return newFiles;
		});
	};

	const updateUploadProgress: progressType = (file, kbComplete) => {
		setSelectedFiles((prevState) => {
			const tempFiles = prevState.slice();

			tempFiles.forEach((tempFile) => {
				if (tempFile.id === file.id) {
					if (typeof kbComplete === 'number' && typeof file.size === 'number') {
						tempFile.percentComplete = Math.ceil((kbComplete / file.size) * 100);
					}
				}
			});

			return tempFiles;
		});
	};

	const completeUploadProgress = (file: FileType) => {
		setSelectedFiles((prevState) => {
			const tempFiles = prevState.slice();

			tempFiles.forEach((tempFile) => {
				if (tempFile.id === file.id) {
					tempFile.uploadInProgress = false;
				}
			});

			return tempFiles;
		});
	};

	const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || !e.target.files.length) return;

		const selectedFile = e.target.files[0];
		let errorMessage = '';
		
		const fileSize = selectedFile.size / 1024 / 1024; // Mb

		if (maxSizeMb && fileSize > maxSizeMb) {
			errorMessage = 'File size is too large';
		}

		const allowedFileTypes = imagesOnly ? imageAllowedFileTypes : allAllowedFileTypes;

		if (!allowedFileTypes.includes(selectedFile.type)) {
			errorMessage = 'File type is not allowed';
		}

		setError(errorMessage);
		if (errorMessage) return;

		await window.grecaptcha.ready(async () => {
			await window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				uploadFilePartTwo(e, selectedFile, token);
			});
		});
	};

	const uploadFilePartTwo = async (e: React.ChangeEvent<HTMLInputElement>, selectedFile: any, token: string) => {
		const credentialsResponse = await apibridge.getUploadCredentials(selectedFile, token);

		if (credentialsResponse.isError) {
			// This error message is too long to display on the site, console.log instead and replace with a placeholder error message
			console.log(
				credentialsResponse.validationErrors.length
					? validationErrorsToString(credentialsResponse.validationErrors)
					: credentialsResponse.message || 'An error has occurred'
			);

			setError('An error has occurred');

			return;
		}

		const credentials = credentialsResponse.result;

		const tempLink = credentials.sasUri;
		const tempId = credentials.fileName;

		const newFile = {
			id: tempId,
			displayFileName: selectedFile.name,
			link: tempLink,
			name: selectedFile.name,
			size: selectedFile.size,
			type: selectedFile.type,
			uploadInProgress: true,
			percentComplete: 0
		};

		setSelectedFiles((prevState) => {
			// if multiple get all selected files, if single clear array
			const newFiles = multiple ? prevState.slice() : [];
			newFiles.push(newFile);
			return newFiles;
		});

		// reset the input
		if (fileInput.current) {
			fileInput.current.value = '';
		}

		const progressCallback: boundProgressType = updateUploadProgress.bind(null, newFile);

		const response = await apibridge.uploadFile(selectedFile, credentials, progressCallback);

		if (!response) {
			handleRemove(newFile.id);
			setError('An error occurred while uploading your file');
		} else {
			setTimeout(() => {
				completeUploadProgress(newFile);
			}, 300);
		}
	};

	React.useEffect(() => {
		if (!filesInitialised && files && files.length) {
			setFilesInitialised(true);
			setSelectedFiles(files);
		}
	}, [files]);

	React.useEffect(() => {
		handleChange();
	}, [selectedFiles]);

	return (
		<div key={keyValue} className="file-upload">
			{label && selectedFiles.length > 0 && <label className="sub-grey-text">{label}</label>}

			<div>
				<FilesList
					files={selectedFiles}
					onChange={multiple ? handleRemoveMultiple : handleRemove}
					multiple={multiple}
				/>
			</div>

			{!!error && <div className="form-validation-error">{error}</div>}

			<div className={disabled ? 'btn btn-secondary disabled full-width' : 'btn btn-secondary full-width'}>
				<input disabled={disabled} ref={fileInput} id={name} name={name} type="file" onChange={uploadFile} />
				<UploadIcon />

				{placeholder || 'Choose file...'}
			</div>

			<div className="info-container">
				<p className="extra-info">
					Max. {maxSizeMb}MB, JPG, PNG{!imagesOnly && ', PDF'}
				</p>

				{!imagesOnly && (
					<Button onClick={() => setDocumentModal(!documentModal)} className="btn-text btn-inline">
						Word or Excel docs?
					</Button>
				)}
			</div>

			<GlobalModal isOpen={documentModal} setIsOpen={setDocumentModal} titleText="Working with Microsoft Documents">
				<>
					<p>
						For security reasons, <strong>ChildAware</strong> does not accept <strong>DOCX</strong> file uploads.
					</p>
					<p>
						If you work with Microsoft Word, converting to PDF is easy, and the general steps to{' '}
						<strong>&apos;Save a PDF&apos;</strong> within a Microsoft application include:
					</p>
					<ol>
						<li>
							Select <strong>&apos;File {'>'} Save a Copy&apos;</strong> or{' '}
							<strong>&apos;File {'>'} Save As&apos;</strong> to begin.
						</li>
						<li>
							After selecting the location to save the file, open the <strong>&apos;Save as type&apos;</strong>{' '}
							drop-down list and select &apos;PDF&apos;.
						</li>
						<li>Select Save.</li>
					</ol>
					<p>
						If you require further help saving your Microsoft documents as PDF, please review the detailed instructions
						via{' '}
						<a
							className="purple-text"
							href="https://support.microsoft.com/en-us/office/save-or-convert-to-pdf-or-xps-in-office-desktop-apps-d85416c5-7d77-4fd6-a216-6f4bf7c7c110#ID0EBBH=Windows"
						>
							Microsoft&apos;s Support page.
						</a>
					</p>
				</>
			</GlobalModal>
		</div>
	);
};

export default FileUpload;
